import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { Copy, Check, Code } from 'lucide-react';

function CodeBlock({ children, className, ...props }) {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const codeString = String(children).replace(/\n$/, '');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = codeString;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!className) {
    // Inline code
    return (
      <code className="chat-inline-code" {...props}>
        {children}
      </code>
    );
  }

  return (
    <div className="chat-code-block">
      <div className="chat-code-header">
        <div className="flex items-center gap-2">
          <Code className="w-3.5 h-3.5" />
          <span>{language || 'code'}</span>
        </div>
        <button
          onClick={handleCopy}
          className="chat-code-copy-btn"
          title={copied ? 'Copied!' : 'Copy code'}
        >
          {copied ? (
            <Check className="w-3.5 h-3.5 text-green-400" />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
      <pre className="chat-code-pre">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
}

export default function ChatMarkdown({ content }) {
  return (
    <div className="chat-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight, rehypeRaw]}
        components={{
          // Headings
          h1: ({ children }) => (
            <h1 className="chat-md-h1">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="chat-md-h2">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="chat-md-h3">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="chat-md-h4">{children}</h4>
          ),

          // Paragraphs
          p: ({ children }) => (
            <p className="chat-md-p">{children}</p>
          ),

          // Lists
          ul: ({ children }) => (
            <ul className="chat-md-ul">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="chat-md-ol">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="chat-md-li">{children}</li>
          ),

          // Code blocks
          code: ({ inline, className, children, ...props }) => {
            if (inline) {
              return (
                <code className="chat-inline-code" {...props}>
                  {children}
                </code>
              );
            }
            return (
              <CodeBlock className={className} {...props}>
                {children}
              </CodeBlock>
            );
          },

          // Pre blocks (wrapper for code blocks)
          pre: ({ children }) => {
            // Let the code component handle the rendering
            return <>{children}</>;
          },

          // Links
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="chat-md-link"
            >
              {children}
            </a>
          ),

          // Blockquotes
          blockquote: ({ children }) => (
            <blockquote className="chat-md-blockquote">
              {children}
            </blockquote>
          ),

          // Tables
          table: ({ children }) => (
            <div className="chat-md-table-wrapper">
              <table className="chat-md-table">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="chat-md-thead">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="chat-md-th">{children}</th>
          ),
          td: ({ children }) => (
            <td className="chat-md-td">{children}</td>
          ),

          // Horizontal rule
          hr: () => <hr className="chat-md-hr" />,

          // Strong and emphasis
          strong: ({ children }) => (
            <strong className="chat-md-strong">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="chat-md-em">{children}</em>
          ),

          // Images
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt}
              className="chat-md-img"
              loading="lazy"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
